import { Trans } from "@lingui/react/macro"
import { Button, Code, CopyButton, Group, Paper, Stack, Text } from "@mantine/core"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

const MCP_URL: Record<IProps["product"], string> = {
  lexicanon: "https://api.lexicanon.com/mcp",
  voxcanon: "https://api.voxcanon.com/mcp",
}

/**
 * Settings card: the MCP URL for Claude plus short how-to for claude.ai,
 * Claude Desktop, and Claude Code. Presentational — the URL comes from the
 * server (it is always on the brand's `api.` host, also for a workspace served
 * on its own custom domain), with the production brand URL as fallback.
 */
export const ConnectClaudeCard = ({ product, mcpUrl: serverMcpUrl }: IProps): React.JSX.Element => {
  const mcpUrl = serverMcpUrl ?? MCP_URL[product]
  const claudeCodeCommand = `claude mcp add --transport http ${product} ${mcpUrl}`

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Text size="sm">
          <Trans>
            Claude can read the meetings of workspaces you belong to and ask questions about them. It cannot change
            anything.
          </Trans>
        </Text>

        <Group gap="xs" wrap="nowrap" className={classes.urlRow}>
          <Code className={classes.url}>{mcpUrl}</Code>
          <CopyButton value={mcpUrl}>
            {({ copied, copy }) => (
              <Button size="compact-sm" variant="light" color={copied ? "success" : "brand"} onClick={copy}>
                {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
              </Button>
            )}
          </CopyButton>
        </Group>

        <div>
          <Text fw={600} size="sm" mb="xs">
            <Trans>claude.ai</Trans>
          </Text>
          <ol className={classes.steps}>
            <li>
              <Text size="sm">
                <Trans>Open Settings → Connectors → Add custom connector.</Trans>
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Trans>Paste the URL above and sign in with your account.</Trans>
              </Text>
            </li>
          </ol>
        </div>

        <div>
          <Text fw={600} size="sm" mb="xs">
            <Trans>Claude Desktop</Trans>
          </Text>
          <Text size="sm">
            <Trans>Open Settings → Connectors, add a custom connector, and paste the same URL.</Trans>
          </Text>
        </div>

        <div>
          <Text fw={600} size="sm" mb="xs">
            <Trans>Claude Code</Trans>
          </Text>
          <Text size="sm">
            <Trans>Run this in a terminal, then sign in when Claude Code opens the browser:</Trans>
          </Text>
          <Code block className={classes.command}>
            {claudeCodeCommand}
          </Code>
        </div>
      </Stack>
    </Paper>
  )
}
